import VisibilityIcon from "@mui/icons-material/Visibility";
import { Stack } from "@mui/material";
import Button from "@mui/material/Button";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import config from "../../config";
import { usePage } from "../../routes";
import { AuthContext } from "../contexts/AuthContext";
import { useIsExpertOrGreater } from "../data/Results/useIsExpertOrGreater";
import { ResultDataPointTypes } from "../shared/enums/Result";
import DeleteConfirmationButton from "../shared/FormLayout/Buttons/DeleteConfirmationButton";
import { getSubstationDisplayName } from "../shared/helpers/ResultUtils";
import { ReportingMeasurement } from "../shared/interfaces/ReportingMeasurement";
import { Result } from "../shared/interfaces/Result";
import { PagedData } from "../shared/interfaces/ResultsAPIInterfaces";
import { ResultViewer } from "./ResultViewer";

interface Props {
  rowData: Result;
  resultData?: PagedData<Result[]>;
  reportingMeasurements?: ReportingMeasurement[];
  customerId: number;
  companyId: number;
  siteId: number;
  onRemoveAnalysis?: (
    captureId: number,
    customerId: number,
    hvSubstationId?: number,
    hvSubstationName?: string,
  ) => void;
}

export function ActionBodyTemplate(props: Props) {
  const { t } = useTranslation();
  const resultViewerPath = usePage(ResultViewer.name)?.path;
  const isPartialResult = props.rowData.dataPointTypeId === ResultDataPointTypes.Partial;
  const result = props.resultData?.result.find((result) => result.captureId === props.rowData.captureId);
  const substationDisplayName = result && getSubstationDisplayName(result);
  const reportMeasurementsByCaptureId = props.reportingMeasurements?.filter(
    (rm) => rm.resultCaptureId === result?.captureId,
  );
  const isEaEmployee = useContext(AuthContext).amIEaEmployee();
  const isExpertOrGreater = useIsExpertOrGreater(
    props.customerId,
    result?.dataValue.SubstationSummary?.SubstationId,
    substationDisplayName,
  );

  var resultType = "bySwitchGear";
  if (isPartialResult) {
    resultType = "byCaptureId";
  } else if (props.rowData.dataValue?.Assets) {
    resultType = "byHVSubstation";
  }

  return (
    <>
      {reportMeasurementsByCaptureId?.length !== undefined && reportMeasurementsByCaptureId.length > 0 ? (
        <Stack direction="row" spacing={1} justifyContent="center">
          <Button
            variant="contained"
            color="primary"
            component={Link}
            to={{
              pathname: resultViewerPath
                ?.replace(":resultType", resultType)
                .replace(":captureId", props.rowData.captureId.toString())
                .replace(
                  ":switchgearAssetId",
                  resultType === "byHVSubstation" && props.rowData.dataValue.SubstationSummary
                    ? props.rowData.dataValue.SubstationSummary?.SubstationId.toString()
                    : props.rowData.assetId.toString(),
                )
                .replace(":customerId", props.customerId.toString())
                .replace(":companyId", props.companyId.toString())
                .replace(":siteId", props.siteId.toString()),
            }}
          >
            <VisibilityIcon />
          </Button>
          {config.reportsEnabled && isEaEmployee && isExpertOrGreater.data?.valueOf() && (
            <DeleteConfirmationButton
              confirmationDialogHeader={t("results.removeAnalysisConfirmationShort")}
              confirmationDialogMessage={t("results.removeAnalysisConfirmation", {
                substation: substationDisplayName,
                time: t("common.formattedDate", {
                  dateString: result?.time,
                }),
              })}
              onConfirmDeleteButtonClick={() =>
                props.onRemoveAnalysis &&
                props.onRemoveAnalysis(
                  props.rowData.captureId,
                  props.customerId,
                  result?.dataValue.SubstationSummary?.SubstationId,
                  substationDisplayName,
                )
              }
            />
          )}
        </Stack>
      ) : (
        <Stack direction="row" spacing={1} justifyContent="center">
          <Button
            variant="contained"
            color="primary"
            component={Link}
            to={{
              pathname: resultViewerPath
                ?.replace(":resultType", resultType)
                .replace(":captureId", props.rowData.captureId.toString())
                .replace(
                  ":switchgearAssetId",
                  resultType === "byHVSubstation" && props.rowData.dataValue.SubstationSummary
                    ? props.rowData.dataValue.SubstationSummary?.SubstationId.toString()
                    : props.rowData.assetId.toString(),
                )
                .replace(":customerId", props.customerId.toString())
                .replace(":companyId", props.companyId.toString())
                .replace(":siteId", props.siteId.toString()),
            }}
          >
            <VisibilityIcon />
          </Button>
          {config.reportsEnabled && isEaEmployee && isExpertOrGreater.data?.valueOf() && (
            <DeleteConfirmationButton
              confirmationDialogHeader={t("results.removeAnalysisConfirmationShort")}
              confirmationDialogMessage={t("results.removeAnalysisConfirmation", {
                substation: substationDisplayName,
                time: t("common.formattedDate", {
                  dateString: result?.time,
                }),
              })}
              onConfirmDeleteButtonClick={() =>
                props.onRemoveAnalysis &&
                props.onRemoveAnalysis(
                  props.rowData.captureId,
                  props.customerId,
                  result?.dataValue.SubstationSummary?.SubstationId,
                  substationDisplayName,
                )
              }
              toolTipText={t("results.noAnalysis")}
              disabled
            />
          )}
        </Stack>
      )}
    </>
  );
}
